<template>
  <v-dialog v-model="dialog" max-width="950" persistent>
    <v-card>
      <v-card-title>
        <v-icon color="primary">mdi-map-marker-path</v-icon>
        <span class="pl-2">Override Trip Start</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <div v-if="loadingTrip" class="text-center py-6">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>

        <div v-else>
          <v-alert v-if="plannedTrip && plannedTrip.override_start_stop_id" type="info" dense text>
            This trip is currently overridden. To select an earlier stop again, use Reset to original schedule.
          </v-alert>

          <v-row>
            <v-col cols="12" md="6">
              <v-select
                v-model="overrideStartStopId"
                :items="stopOptions"
                item-text="text"
                item-value="value"
                label="New start stop"
                outlined
                dense
              ></v-select>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="overrideStartTime"
                label="New start time"
                type="time"
                :min="minTime"
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>

          <v-checkbox
            v-model="sendNotifications"
            label="Send missed pickup notifications (respect user setting)"
          ></v-checkbox>

          <v-alert v-if="errorMessage" type="error" dense text>
            {{ errorMessage }}
          </v-alert>

          <div class="mt-2">
            <v-btn
              color="primary"
              :loading="previewLoading"
              :disabled="!canPreview"
              @click="preview"
            >
              Preview impact
            </v-btn>
            <v-btn
              class="ml-2"
              color="success"
              :loading="confirmLoading"
              :disabled="!canConfirm"
              @click="confirmApply"
            >
              Confirm & Apply
            </v-btn>
            <v-btn
              class="ml-2"
              color="warning"
              :loading="resetLoading"
              @click="resetToOriginal"
            >
              Reset to original
            </v-btn>
          </div>

          <div v-if="previewData" class="mt-6">
            <v-alert type="info" dense text>
              Impacted users (will be marked missed): {{ previewData.impacted_count }}
            </v-alert>

            <v-simple-table dense>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Pickup stop</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="res in previewData.impacted_reservations" :key="res.id">
                  <td>{{ res.student ? res.student.name : res.student_id }}</td>
                  <td>
                    {{
                      res.first_stop
                        ? (res.first_stop.name || res.first_stop.address)
                        : res.start_stop_id
                    }}
                  </td>
                </tr>
                <tr v-if="previewData.impacted_reservations.length === 0">
                  <td colspan="2" class="text-center text-muted py-4">No impacted users</td>
                </tr>
              </tbody>
            </v-simple-table>

            <v-divider class="my-4"></v-divider>

            <v-alert type="info" dense text>
              New timetable (remaining stops)
            </v-alert>

            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Stop</th>
                  <th>Old</th>
                  <th>New</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="d in previewData.new_planned_trip_details" :key="d.id">
                  <td>{{ d.stop ? (d.stop.name || d.stop.address) : d.stop_id }}</td>
                  <td>{{ d.old_planned_timestamp }}</td>
                  <td><b>{{ d.new_planned_timestamp }}</b></td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "OverrideStartDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    trip: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loadingTrip: false,
      plannedTrip: null,

      overrideStartStopId: null,
      overrideStartTime: null,
      sendNotifications: false,

      previewLoading: false,
      confirmLoading: false,
      resetLoading: false,
      loadingMissedReservations: false,

      previewData: null,
      missedReservations: null,
      errorMessage: null,
    };
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    isTripToday() {
      if (!this.plannedTrip || !this.plannedTrip.planned_date) return false;
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      return this.plannedTrip.planned_date === today;
    },
    minTime() {
      if (!this.isTripToday) return null;
      const now = new Date();
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    },
    stopOptions() {
      if (!this.plannedTrip || !this.plannedTrip.planned_trip_detail) return [];
      const details = this.plannedTrip.planned_trip_detail
        .filter((d) => d && d.stop && d.stop_id);

      // Override Trip Start should not allow selecting:
      // 1. The first stop (index 0) - it's already the start, no override needed
      // 2. The final stop (last index) - it's the destination, can't start from there
      // So we exclude both first and last stops
      const eligibleDetails = details.length > 2 
        ? details.slice(1, -1)  // Exclude first (0) and last (-1)
        : [];  // If 2 or fewer stops, no eligible stops (need at least 3 stops to override)

      return eligibleDetails
        .map((d) => {
          const label = d.stop.name || d.stop.address || `Stop ${d.stop_id}`;
          return {
            value: d.stop_id,
            text: `${label} (${d.planned_timestamp})`,
          };
        });
    },
    canPreview() {
      return (
        !!this.trip &&
        !!this.trip.id &&
        !!this.overrideStartStopId &&
        !!this.overrideStartTime &&
        !this.previewLoading &&
        !this.confirmLoading &&
        !this.resetLoading
      );
    },
    canConfirm() {
      return this.canPreview && this.previewData != null;
    },
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.init();
      } else {
        this.resetState();
      }
    },
    trip() {
      if (this.value) {
        this.init();
      }
    },
    overrideStartStopId() {
      // Any change invalidates previous preview
      this.previewData = null;
      this.errorMessage = null;
    },
    overrideStartTime(newTime) {
      this.previewData = null;
      this.errorMessage = null;
      
      // Additional validation for today's trips
      if (this.isTripToday && newTime) {
        const now = new Date();
        const [hours, minutes] = newTime.split(':');
        const selectedTime = new Date();
        selectedTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        
        if (selectedTime < now) {
          this.errorMessage = 'Cannot set start time in the past for today\'s trip.';
        }
      }
    },
  },
  methods: {
    resetState() {
      this.loadingTrip = false;
      this.plannedTrip = null;
      this.overrideStartStopId = null;
      this.overrideStartTime = null;
      this.sendNotifications = false;
      this.previewLoading = false;
      this.confirmLoading = false;
      this.resetLoading = false;
      this.loadingMissedReservations = false;
      this.previewData = null;
      this.missedReservations = null;
      this.errorMessage = null;
    },
    close() {
      this.dialog = false;
    },
    init() {
      this.resetState();
      this.fetchPlannedTrip();
    },
    fetchPlannedTrip() {
      if (!this.trip || !this.trip.id) return;
      this.loadingTrip = true;
      axios
        .get(`/planned-trips/${this.trip.id}`)
        .then((response) => {
          this.plannedTrip = response.data.trip;
        })
        .catch((error) => {
          console.log(error);
          this.errorMessage =
            (error.response && error.response.data && error.response.data.message) ||
            "Failed to load planned trip details";
        })
        .then(() => {
          this.loadingTrip = false;
        });
    },
    preview() {
      this.errorMessage = null;
      this.previewLoading = true;
      axios
        .post(`/planned-trips/${this.trip.id}/override-start/preview`, {
          override_start_stop_id: this.overrideStartStopId,
          override_start_time: this.overrideStartTime,
        })
        .then((response) => {
          this.previewData = response.data;
        })
        .catch((error) => {
          console.log(error);
          this.previewData = null;
          this.errorMessage =
            (error.response && error.response.data && error.response.data.message) ||
            "Failed to preview override impact";
        })
        .then(() => {
          this.previewLoading = false;
        });
    },
    confirmApply() {
      if (!this.trip || !this.trip.id || !this.previewData) return;

      const impacted = Number(
        (this.previewData && this.previewData.impacted_count) || 0
      );

      const doApply = () => {
        this.errorMessage = null;
        this.confirmLoading = true;

        axios
          .post(`/planned-trips/${this.trip.id}/override-start/confirm`, {
            override_start_stop_id: this.overrideStartStopId,
            override_start_time: this.overrideStartTime,
            send_notifications: this.sendNotifications,
          })
          .then((response) => {
            let appliedImpacted = 0;
            if (
              response &&
              response.data &&
              response.data.impacted_count != null
            ) {
              appliedImpacted = Number(response.data.impacted_count) || 0;
            }

            this.$notify({
              title: "Success",
              text:
                appliedImpacted > 0
                  ? `Trip start override applied. ${appliedImpacted} reservation(s) were skipped (marked missed).`
                  : "Trip start override applied",
              type: "success",
            });
            this.$emit("updated");
            this.close();
          })
          .catch((error) => {
            console.log(error);
            this.errorMessage =
              (error.response && error.response.data && error.response.data.message) ||
              "Failed to apply override";
          })
          .then(() => {
            this.confirmLoading = false;
          });
      };

      if (impacted > 0) {
        const reservations = this.previewData.impacted_reservations || [];
        let usersList = "";
        if (reservations.length > 0) {
          usersList = "<div style='max-height: 300px; overflow-y: auto; margin-top: 10px;'>";
          usersList += "<table style='width: 100%; border-collapse: collapse;'>";
          usersList += "<thead><tr style='background-color: #f5f5f5;'><th style='padding: 8px; text-align: left; border: 1px solid #ddd;'>User</th><th style='padding: 8px; text-align: left; border: 1px solid #ddd;'>Start Point</th></tr></thead>";
          usersList += "<tbody>";
          reservations.forEach((res) => {
            const userName = res.student ? res.student.name : `User #${res.student_id}`;
            const stopName = res.first_stop
              ? res.first_stop.name || res.first_stop.address || `Stop #${res.start_stop_id}`
              : `Stop #${res.start_stop_id}`;
            usersList += `<tr><td style='padding: 8px; border: 1px solid #ddd;'>${userName}</td><td style='padding: 8px; border: 1px solid #ddd;'>${stopName}</td></tr>`;
          });
          usersList += "</tbody></table></div>";
        }

        this.$swal({
          title: "Apply override start?",
          html: `This will skip the first stops and mark <b>${impacted}</b> reservation(s) as missed.<br/><br/>` +
                `<b>Users who will be skipped:</b>${usersList}<br/>` +
                `Continue?`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, apply",
          cancelButtonText: "Cancel",
          width: "600px",
        }).then((result) => {
          if (!result.isConfirmed) return;
          doApply();
        });
        return;
      }

      doApply();
    },
    async resetToOriginal() {
      if (!this.trip || !this.trip.id) return;

      // Fetch missed reservations before showing confirmation
      this.loadingMissedReservations = true;
      try {
        const response = await axios.get(`/planned-trips/${this.trip.id}/missed-reservations`);
        if (response.data && response.data.success) {
          this.missedReservations = response.data.missed_reservations || [];
        } else {
          this.missedReservations = [];
        }
      } catch (error) {
        console.log("Failed to fetch missed reservations:", error);
        this.missedReservations = [];
      } finally {
        this.loadingMissedReservations = false;
      }

      let usersList = "";
      const missedCount = this.missedReservations ? this.missedReservations.length : 0;
      
      if (missedCount > 0 && this.missedReservations) {
        usersList = "<div style='max-height: 300px; overflow-y: auto; margin-top: 10px;'>";
        usersList += "<table style='width: 100%; border-collapse: collapse;'>";
        usersList += "<thead><tr style='background-color: #f5f5f5;'><th style='padding: 8px; text-align: left; border: 1px solid #ddd;'>User</th><th style='padding: 8px; text-align: left; border: 1px solid #ddd;'>Start Point</th></tr></thead>";
        usersList += "<tbody>";
        this.missedReservations.forEach((res) => {
          const userName = res.student ? res.student.name : `User #${res.student_id}`;
          const stop = res.first_stop || res.firstStop;
          const stopName = stop
            ? stop.name || stop.address || `Stop #${res.start_stop_id}`
            : `Stop #${res.start_stop_id}`;
          usersList += `<tr><td style='padding: 8px; border: 1px solid #ddd;'>${userName}</td><td style='padding: 8px; border: 1px solid #ddd;'>${stopName}</td></tr>`;
        });
        usersList += "</tbody></table></div>";
      }

      this.$swal({
        title: "Reset trip schedule to original?",
        html: "This will:<br/>" +
              "• Restore the original stop list and pickup times<br/>" +
              "• <b>Restore all skipped users</b> (mark them as active again)<br/>" +
              "• Remove the override start point<br/><br/>" +
              (missedCount > 0
                ? `<b>Users who will be restored (${missedCount}):</b>${usersList}<br/>`
                : "No skipped users to restore.<br/>") +
              "All users who were marked as missed due to the override will be able to be picked up again at their original stops.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, reset to original",
        cancelButtonText: "Cancel",
        width: missedCount > 0 ? "600px" : "500px",
      }).then((result) => {
        if (!result.isConfirmed) return;

        this.errorMessage = null;
        this.resetLoading = true;
        axios
          .post(`/planned-trips/${this.trip.id}/override-start/reset`)
          .then(() => {
            this.$notify({
              title: "Success",
              text: missedCount > 0
                ? `Trip schedule reset to original. ${missedCount} skipped user(s) have been restored.`
                : "Trip schedule reset to original.",
              type: "success",
            });
            this.$emit("updated");
            this.close();
          })
          .catch((error) => {
            console.log(error);
            this.errorMessage =
              (error.response && error.response.data && error.response.data.message) ||
              "Failed to reset override";
          })
          .then(() => {
            this.resetLoading = false;
          });
      });
    },
  },
};
</script>


